import React, { useState } from "react";
import EmailForm from "./EmailForm";

const ServicesOverview = () => {
  const [isEmailFormOpen, setIsEmailFormOpen] = useState(false);
  const [selectedService, setSelectedService] = useState("");

  const handleScheduleConsultation = () => {
    setSelectedService("Free Consultation");
    setIsEmailFormOpen(true);
  };

  const handleRequestConsultation = (serviceName) => {
    setSelectedService(serviceName);
    setIsEmailFormOpen(true);
  };

  const closeEmailForm = () => {
    setIsEmailFormOpen(false);
    setSelectedService("");
  };
  const services = [
    {
      id: "automation-implementation",
      icon: "🏗️",
      title: "Automation Framework Implementation",
      description:
        "End-to-end automation framework setup with modern tools and best practices",
      features: [
        "Complete framework architecture design and implementation",
        "TypeScript + Playwright or Java + Selenium setup",
        "Page Object Model and design pattern implementation",
        "CI/CD integration with GitHub Actions and Jenkins",
        "Comprehensive reporting with Allure and custom dashboards",
      ],
      deliverables: "Production-ready framework in 2-4 weeks",
      ideal: "Startups and mid-size teams starting automation journey",
    },
    {
      id: "ai-qa-agent",
      icon: "🤖",
      title: "AI-Powered QA Agent",
      description:
        "Revolutionary AI system that generates Page Object Models and comprehensive test suites",
      features: [
        "Automated Page Object Model generation from UI analysis",
        "Intelligent test case creation from user stories",
        "Multi-framework support (Playwright, Selenium, Cypress)",
        "Natural language to test code conversion",
        "Continuous learning and optimization capabilities",
      ],
      deliverables: "70% faster test development, 90% coverage improvement",
      ideal: "Teams looking to accelerate automation and scale testing",
    },
    {
      id: "quality-assessment",
      icon: "🎯",
      title: "Quality Gap Analysis & Strategy",
      description:
        "Comprehensive quality assessment to identify high-impact improvement opportunities",
      features: [
        "Current testing process evaluation and gap analysis",
        "Quality metrics assessment and benchmarking",
        "Automation ROI analysis and prioritization",
        "Test strategy roadmap and implementation plan",
        "Risk assessment and mitigation strategies",
      ],
      deliverables: "Detailed quality roadmap with 6-month action plan",
      ideal: "Organizations seeking strategic quality transformation",
    },
    {
      id: "team-mentoring",
      icon: "👥",
      title: "Team Mentoring & Training",
      description:
        "Hands-on mentoring to build internal QA expertise and sustainable practices",
      features: [
        "Test strategy and automation design patterns training",
        "Code review and best practices workshops",
        "Quality metrics and reporting establishment",
        "Knowledge transfer and documentation",
        "Ongoing support and guidance programs",
      ],
      deliverables: "Self-sufficient QA team within 3 months",
      ideal: "Teams wanting to build internal automation expertise",
    },
  ];

  return (
    <>
      <section id="services" className="py-20 bg-gray-900/30">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Our <span className="text-gradient">Consulting Services</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Accelerate your QA maturity with proven methodologies,
              cutting-edge AI technology, and strategic guidance tailored for
              fast-growing engineering teams.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 max-w-7xl mx-auto">
            {services.map((service, index) => (
              <div
                key={service.id}
                className="bg-gray-800/50 rounded-xl p-8 border border-gray-700/50 hover:border-gray-600/50 transition-all duration-300"
              >
                <div className="flex items-start space-x-4 mb-6">
                  <div className="text-4xl">{service.icon}</div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-white mb-2">
                      {service.title}
                    </h3>
                    <p className="text-gray-400 text-lg">
                      {service.description}
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-3 flex items-center">
                      <span className="text-green-400 mr-2">✓</span>
                      Key Features
                    </h4>
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <li
                          key={idx}
                          className="text-gray-300 flex items-start"
                        >
                          <span className="text-green-400 mr-3 mt-1 text-xs">
                            ▶
                          </span>
                          <span className="text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="pt-4 border-t border-gray-700/50">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <h5 className="text-sm font-semibold text-blue-400 mb-1">
                          Expected Outcomes
                        </h5>
                        <p className="text-gray-400 text-sm">
                          {service.deliverables}
                        </p>
                      </div>
                      <div>
                        <h5 className="text-sm font-semibold text-purple-400 mb-1">
                          Ideal For
                        </h5>
                        <p className="text-gray-300 text-sm">{service.ideal}</p>
                      </div>
                    </div>
                  </div>

                  <div className="pt-4">
                    <button
                      onClick={() => handleRequestConsultation(service.title)}
                      className="w-full px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition"
                    >
                      Request Consultation
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Call to Action */}
          <div className="mt-16 text-center">
            <div className="bg-gradient-to-r from-gray-800/50 to-gray-900/30 rounded-2xl p-8 md:p-12 border border-gray-700/50">
              <h3 className="text-3xl font-bold text-white mb-4">
                Ready to Transform Your QA Practice?
              </h3>
              <p className="text-xl text-gray-400 mb-8 max-w-2xl mx-auto">
                Let's discuss how our AI-powered automation solutions and
                strategic consulting can accelerate your testing maturity and
                deliver measurable business impact.
              </p>
              <div className="flex justify-center">
                <button
                  onClick={handleScheduleConsultation}
                  className="px-8 py-4 bg-gradient-to-r from-blue-400 to-blue-500 text-black font-bold rounded-lg hover:from-green-500 hover:to-blue-600 transition transform hover:scale-105"
                >
                  Schedule Free Consultation
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Email Form Modal */}
      <EmailForm
        isOpen={isEmailFormOpen}
        onClose={closeEmailForm}
        selectedFramework={selectedService}
      />
    </>
  );
};

export default ServicesOverview;
